import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../../services/common.service";
import { useCurrencyMaster } from "../../../hooks/useCurrencyMaster";
import { useProductDetailsMaster } from "../../../hooks/useProductDetailsMaster";
import '../../../assets/styles/purchase_order_list.scss';

const MARKETPLACE_PAYMENT_MARKER = '--- Marketplace checkout payment ---';

const resolveMarketplaceDirectOrder = (order, poInfo) => {
  if (poInfo?.is_marketplace_direct_order === true) return true;
  if (order?.is_marketplace_direct_order === true) return true;
  const quotationNumber = order?.quotation_number || poInfo?.quotation_number || '';
  const rfq = order?.rfqData || order?.rfq_data;
  const specialInstructions = poInfo?.special_instructions || order?.special_instructions || '';
  return (
    String(quotationNumber).startsWith('MKT-') &&
    (rfq?.source_product_id != null || String(specialInstructions).includes('Catalog checkout for'))
  );
};

const resolveMarketplacePaymentStatus = (poInfo) => {
  if (poInfo?.payment_status === 'paid' || poInfo?.payment_status === 'pending') {
    return poInfo.payment_status;
  }
  return String(poInfo?.special_instructions || '').includes(MARKETPLACE_PAYMENT_MARKER) ? 'paid' : 'pending';
};

export default function PurchaseOrderList() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("create");
  const [createOrders, setCreateOrders] = useState([]);
  const [acceptedOrders, setAcceptedOrders] = useState([]);
  const [rejectedOrders, setRejectedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRFQs, setExpandedRFQs] = useState({});
  const [showAcceptedDetailsModal, setShowAcceptedDetailsModal] = useState(false);
  const [acceptedDetailsData, setAcceptedDetailsData] = useState(null);
  const [loadingAcceptedDetails, setLoadingAcceptedDetails] = useState(false);
  const [showRejectedDetailsModal, setShowRejectedDetailsModal] = useState(false);
  const [rejectedDetailsData, setRejectedDetailsData] = useState(null);
  const [loadingRejectedDetails, setLoadingRejectedDetails] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPOData, setPreviewPOData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Master Data Hooks
  const { currencyOptions } = useCurrencyMaster();
  const { attributeUnitOptions } = useProductDetailsMaster();

  useEffect(() => {
    fetchPurchaseOrders();
  }, [activeTab]);

  // Helper function to fetch full PO details with line items and RFQ data
  const fetchFullPODetails = async (order) => {
    try {
      const poId = order.id || order.purchase_order_id;
      if (!poId) return order;
      
      // Preserve RFQ ID and quotation ID from original order
      const originalRfqId = order.rfq_id;
      const originalQuotationId = order.quotation_id;
      
      // Fetch full PO details to get line items
      const fullPOResponse = await CommonService.get(`/purchase-order/${poId}`);
      if (fullPOResponse && fullPOResponse.success && fullPOResponse.data) {
        // Prioritize RFQ ID from original order, fallback to full PO response
        const finalRfqId = originalRfqId || fullPOResponse.data.rfq_id || null;
        
        // Fetch RFQ data to get product name from rfq_request table
        let rfqData = null;
        if (finalRfqId) {
          try {
            // Extract numeric ID from REQ-{id} format if needed
            const rfqIdNum = typeof finalRfqId === 'string' && finalRfqId.startsWith('REQ-')
              ? parseInt(finalRfqId.replace('REQ-', ''), 10)
              : parseInt(finalRfqId, 10);
            
            if (!isNaN(rfqIdNum)) {
              const rfqResponse = await CommonService.get(`/rfq/${rfqIdNum}`);
              if (rfqResponse && rfqResponse.success && rfqResponse.data) {
                rfqData = rfqResponse.data;
              }
            }
          } catch (rfqError) {
            console.warn(`Error fetching RFQ ${finalRfqId} for PO ${poId}:`, rfqError);
          }
        }
        
        return {
          ...order,
          ...fullPOResponse.data,
          // Preserve RFQ ID and quotation ID for grouping (prioritize original values)
          rfq_id: finalRfqId,
          quotation_id: originalQuotationId || fullPOResponse.data.quotation_id || null,
          // Include RFQ data for product name extraction
          rfq_data: rfqData,
          // Ensure line items are available
          line_items: fullPOResponse.data.line_items || fullPOResponse.data.po_data?.lineItems || [],
          po_data: {
            ...order.po_data,
            ...fullPOResponse.data.po_data,
            lineItems: fullPOResponse.data.line_items || fullPOResponse.data.po_data?.lineItems || []
          }
        };
      }
      return order;
    } catch (error) {
      console.error(`Error fetching full PO details for PO ${order.id}:`, error);
      return order;
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      
      // Fetch all purchase orders first (needed for all tabs to show accurate counts)
      let allPurchaseOrders = [];
      try {
        const poResponse = await CommonService.get("/purchase-order/buyer/my-orders");
        if (poResponse && poResponse.success && poResponse.data) {
          allPurchaseOrders = Array.isArray(poResponse.data) ? poResponse.data : [];
        }
      } catch (error) {
        console.error('Error fetching purchase orders:', error);
      }
      
      // Filter all order types regardless of active tab to show accurate counts
      // Accepted orders: POs accepted by seller
      const acceptedOrdersList = allPurchaseOrders.filter(order => {
        const poStatus = (order.po_status || '').toString().toLowerCase();
        const poResponseStatus = (order.po_response_status || '').toString().toLowerCase();
        return poStatus === 'accepted' || poResponseStatus === 'accepted';
      });
      
      // Fetch full PO details (including line items) for accepted orders
      // This is needed for displaying product information in the cards
      const acceptedWithDetails = await Promise.all(
        acceptedOrdersList.map(order => fetchFullPODetails(order))
      );
      
      setAcceptedOrders(acceptedWithDetails);
      
      // Rejected orders: POs rejected by seller
      const rejectedOrdersList = allPurchaseOrders.filter(order => 
        (order.po_response_status && order.po_response_status.toLowerCase() === 'rejected') ||
        (order.po_status && order.po_status.toLowerCase() === 'rejected')
      );
      
      // Fetch full PO details (including line items and RFQ data) for rejected orders
      // This is needed for displaying product information in the cards
      const rejectedWithDetails = await Promise.all(
        rejectedOrdersList.map(order => fetchFullPODetails(order))
      );
      
      setRejectedOrders(rejectedWithDetails);
      
      // For Create Order tab: Fetch only accepted quotations (with their PO info if exists)
      // Do NOT show standalone draft POs - only show quotations that may have associated POs
      if (activeTab === 'create') {
        const results = [];
        
        // Use already fetched purchase orders to map quotation IDs to PO information
        // This includes all POs regardless of status (submitted, accepted, rejected, etc.)
        let quotationToPOMap = new Map();
        
        // Map quotation IDs to their PO information (for displaying PO numbers and status)
        // Include ALL POs (draft, submitted, etc.) so we can show PO information on quotation cards
        allPurchaseOrders.forEach(order => {
          const quotationId = order.quotation_id || order.quotationId;
          if (quotationId) {
            const poId = order.id || order.purchase_order_id || order.purchaseOrderId;
            if (poId) {
              // If multiple POs exist for same quotation, prioritize non-accepted ones for Create PO tab
              // But if already mapped, only update if current PO is draft (to show Edit option)
              const existingPoInfo = quotationToPOMap.get(quotationId);
              const poStatus = (order.po_status || '').toString().toLowerCase();
              const poResponseStatus = (order.po_response_status || '').toString().toLowerCase();
              
              // Only map if:
              // 1. Not already mapped, OR
              // 2. Current PO is draft and existing is not draft (prioritize draft for Edit option)
              if (!existingPoInfo || (poStatus === 'draft' && existingPoInfo.po_status?.toLowerCase() !== 'draft')) {
                quotationToPOMap.set(quotationId, {
                  po_id: poId,
                  po_number: order.po_number,
                  po_status: order.po_status,
                  po_response_status: order.po_response_status,
                  special_instructions: order.special_instructions || null,
                  is_marketplace_direct_order: order.is_marketplace_direct_order === true,
                  payment_status: order.payment_status || null,
                });
              }
            }
          }
        });
        
        // Fetch accepted quotations
        try {
          const quotationResponse = await CommonService.get("/quotation/buyer/my-quotations");
          
          if (quotationResponse && quotationResponse.success && quotationResponse.data) {
            const allQuotations = Array.isArray(quotationResponse.data) ? quotationResponse.data : [];
            
            // Filter only accepted quotations
            const acceptedQuotations = allQuotations.filter(quotation => {
              const isAccepted = quotation.quote_status && quotation.quote_status.toLowerCase() === 'accepted';
              return isAccepted;
            });
            
            // Fetch RFQ details for each accepted quotation and attach PO information
            const quotationsWithRFQ = await Promise.all(
              acceptedQuotations.map(async (quotation) => {
                try {
                  let rfqData = null;
                  if (quotation.rfq_id) {
                    const rfqResponse = await CommonService.get(`/rfq/${quotation.rfq_id}`);
                    if (rfqResponse && rfqResponse.success && rfqResponse.data) {
                      rfqData = rfqResponse.data;
                    }
                  }
                  
                  // Attach PO information if it exists
                  const poInfo = quotationToPOMap.get(quotation.quotation_id);
                  
                  return {
                    ...quotation,
                    rfqData: rfqData,
                    poInfo: poInfo || null
                  };
                } catch (error) {
                  console.error(`Error fetching RFQ ${quotation.rfq_id} for quotation ${quotation.quotation_id}:`, error);
                  const poInfo = quotationToPOMap.get(quotation.quotation_id);
                  return {
                    ...quotation,
                    poInfo: poInfo || null
                  };
                }
              })
            );
            
            // Filter out quotations that have POs accepted or rejected by seller
            // Show all accepted quotations except those with seller-accepted or seller-rejected POs
            // Seller-accepted POs should appear in the Accepted tab, not Create PO tab
            // Seller-rejected POs should appear in the Rejected tab, not Create PO tab
            const eligibleQuotations = quotationsWithRFQ.filter(q => {
              const poInfo = q.poInfo;
              if (!poInfo) {
                return true; // No PO, show in Create PO tab
              }
              
              // Check if PO has been accepted or rejected by seller
              const poStatus = (poInfo.po_status || '').toString().toLowerCase();
              const poResponseStatus = (poInfo.po_response_status || '').toString().toLowerCase();
              
              // Exclude if PO is accepted by seller
              if (poStatus === 'accepted' || poResponseStatus === 'accepted') {
                return false; // Exclude - PO accepted by seller, should be in Accepted tab
              }
              
              // Exclude if PO is rejected by seller
              if (poStatus === 'rejected' || poResponseStatus === 'rejected') {
                return false; // Exclude - PO rejected by seller, should be in Rejected tab
              }
              
              // Include all other cases (Draft, Submitted, Pending, or no PO)
              return true;
            });
            
            // Remove duplicates by quotation_id to ensure only one card per quotation
            const uniqueQuotations = [];
            const seenQuotationIds = new Set();
            
            eligibleQuotations.forEach(q => {
              const quotationId = q.quotation_id || q.quotationId;
              if (quotationId && !seenQuotationIds.has(quotationId)) {
                seenQuotationIds.add(quotationId);
                uniqueQuotations.push({ ...q, type: 'quotation' });
              }
            });
            
            results.push(...uniqueQuotations);
          }
        } catch (error) {
          console.error('Error fetching quotations:', error);
        }
        
        // Do NOT add standalone draft POs - only show quotations with their PO info
        // This prevents duplicate display of the same PO
        
        setCreateOrders(results);
      }
      
      // Accepted and Rejected orders are already fetched and set at the beginning
      // No need to fetch again when switching tabs
    } catch (error) {
      console.error("Error fetching data:", error);
      
      // Handle 401 Unauthorized
      if (error?.response?.status === 401) {
        toast.error('Your session has expired. Please login again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
        return;
      }
      
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch data";
      
      // Only show error if it's not a 404 (endpoint might not exist yet)
      if (error?.response?.status !== 404) {
        toast.error(errorMessage);
      }
      
      // Set empty arrays on error
      setCreateOrders([]);
      setAcceptedOrders([]);
      setRejectedOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateForPreview = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-IN', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const isInternationalTrade = (scenario) => {
    if (!scenario) return false;
    return scenario === '→IN Import to India (Abroad → India)' ||
           scenario === 'IN→ Export from India (India → Abroad)' ||
           scenario === 'Third Country (Abroad → Abroad)';
  };

  const formatCurrency = (amount, currencyCode = "INR") => {
    if (!amount) return "N/A";
    const formatted = new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
    
    // Get currency symbol if available
    let symbol = "";
    if (currencyOptions && currencyOptions.length > 0) {
      const currency = currencyOptions.find(
        opt => opt.currencyCode === currencyCode || String(opt.value) === String(currencyCode)
      );
      symbol = currency?.currencySymbol || "";
    }
    
    return `${symbol}${formatted}`;
  };

  // Helper function to get CGST percentage, calculating from amount if percentage is 0
  const getCgstPercentage = (item, tradeScenario = '') => {
    let cgst = parseFloat(item.cgst || item.cgstPercent || 0);
    
    // If CGST percentage is 0, try to calculate from amount or check legacy fields
    if (cgst === 0) {
      // First, try to calculate from cgst_amount
      const cgstAmount = parseFloat(item.cgst_amount || item.cgstAmount || 0);
      // Calculate taxable amount (FOB value) properly
      const subtotal = parseFloat(item.subtotal || (item.quantity * item.unit_price) || (item.quantity * item.unitPrice) || 0);
      const discountAmount = parseFloat(item.discount_amount || item.discountAmount || (subtotal * (item.discount || 0) / 100) || 0);
      const taxableAmount = parseFloat(item.fob_value || item.fobValue || (subtotal - discountAmount) || 0);
      
      if (taxableAmount > 0 && cgstAmount > 0) {
        cgst = (cgstAmount / taxableAmount) * 100;
      } else {
        // Legacy: Check if CGST is stored in igst field for intra-state trades
        // This happens when quotations were created with CGST in igst field
        const igst = parseFloat(item.igst || item.igstPercent || 0);
        const scenario = tradeScenario || previewPOData?.trade_scenario || previewPOData?.po_data?.tradeScenario || '';
        
        // Only use igst as cgst for intra-state trades
        if (igst > 0 && (scenario === 'IN Intra-State (Same State in India)' || scenario.includes('Intra-State'))) {
          cgst = igst;
        }
      }
    }
    
    return cgst;
  };

  // Helper function to get SGST percentage, calculating from amount if percentage is 0
  const getSgstPercentage = (item) => {
    let sgst = parseFloat(item.sgst || item.sgstPercent || 0);
    
    // If SGST percentage is 0 but we have sgst_amount, calculate percentage from amount
    if (sgst === 0 && (item.sgst_amount || item.sgstAmount)) {
      const sgstAmount = parseFloat(item.sgst_amount || item.sgstAmount || 0);
      const taxableAmount = parseFloat(item.fob_value || item.fobValue || item.subtotal || 0);
      if (taxableAmount > 0 && sgstAmount > 0) {
        sgst = (sgstAmount / taxableAmount) * 100;
      }
    }
    
    return sgst;
  };

  const calculatePreviewTotals = (poData, lineItems) => {
    if (!lineItems || lineItems.length === 0 || !poData) {
      return {
        subtotal: 0,
        totalDiscount: 0,
        taxableAmount: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        cess: 0,
        basicCustomsDuty: 0,
        socialWelfareSurcharge: 0,
        assessableValue: 0,
        igstOnAssessable: 0,
        tax: 0,
        grandTotal: 0
      };
    }

    const tradeScenario = poData.trade_scenario || poData.tradeScenario || '';
    const exportType = poData.export_type || poData.exportType || '';
    const cifConfig = poData.cif_config || poData.cifConfig || {};

    // Calculate subtotal
    const subtotal = lineItems.reduce((sum, item) => {
      const itemSubtotal = parseFloat(item.subtotal || item.quantity * (item.unit_price || item.unitPrice) || 0);
      return sum + itemSubtotal;
    }, 0);

    // Calculate total discount
    const totalDiscount = lineItems.reduce((sum, item) => {
      return sum + parseFloat(item.discount_amount || item.discountAmount || 0);
    }, 0);

    // Taxable amount (FOB value)
    const taxableAmount = subtotal - totalDiscount;

    // Helper to get item taxable amount
    const getItemTaxable = (item) => {
      const itemSubtotal = parseFloat(item.subtotal || item.quantity * (item.unit_price || item.unitPrice) || 0);
      const itemDiscount = parseFloat(item.discount_amount || item.discountAmount || 0);
      return parseFloat(item.fob_value || item.fobValue || (itemSubtotal - itemDiscount));
    };

    // Calculate CGST (intra-state only)
    const cgst = lineItems.reduce((sum, item) => {
      let cgstAmount = parseFloat(item.cgst_amount || item.cgstAmount || 0);
      
      // If cgst_amount is 0 or missing, calculate from percentage
      if ((!cgstAmount || cgstAmount === 0) && (item.cgst || item.cgstPercent)) {
        const itemTaxable = getItemTaxable(item);
        const cgstPercent = getCgstPercentage(item, tradeScenario);
        if (cgstPercent > 0 && itemTaxable > 0) {
          cgstAmount = itemTaxable * (cgstPercent / 100);
        }
      }
      
      return sum + cgstAmount;
    }, 0);

    // Calculate SGST (intra-state only)
    const sgst = lineItems.reduce((sum, item) => {
      let sgstAmount = parseFloat(item.sgst_amount || item.sgstAmount || 0);
      
      // If sgst_amount is 0 or missing, calculate from percentage
      if ((!sgstAmount || sgstAmount === 0) && (item.sgst || item.sgstPercent)) {
        const itemTaxable = getItemTaxable(item);
        const sgstPercent = getSgstPercentage(item);
        if (sgstPercent > 0 && itemTaxable > 0) {
          sgstAmount = itemTaxable * (sgstPercent / 100);
        }
      }
      
      return sum + sgstAmount;
    }, 0);

    // Calculate IGST
    const igst = lineItems.reduce((sum, item) => {
      let igstAmount = parseFloat(item.igst_amount || item.igstAmount || 0);
      
      // If igst_amount is 0 or missing, calculate from percentage
      if ((!igstAmount || igstAmount === 0) && (item.igst || item.igstPercent)) {
        const itemTaxable = getItemTaxable(item);
        const igstPercent = parseFloat(item.igst || item.igstPercent || 0);
        if (igstPercent > 0 && itemTaxable > 0) {
          igstAmount = itemTaxable * (igstPercent / 100);
        }
      }
      
      return sum + igstAmount;
    }, 0);

    // Calculate CESS
    const cess = lineItems.reduce((sum, item) => {
      let cessAmount = parseFloat(item.cess_amount || item.cessAmount || 0);
      
      // If cess_amount is 0 or missing, calculate from percentage
      if ((!cessAmount || cessAmount === 0) && (item.cess || item.cessPercent)) {
        const cessPercent = parseFloat(item.cess || item.cessPercent || 0);
        
        // For import scenarios, CESS is calculated on assessable value
        if (isInternationalTrade(tradeScenario) && tradeScenario.includes('Import')) {
          const assessableValue = parseFloat(item.assessable_value || item.assessableValue || 0);
          if (cessPercent > 0 && assessableValue > 0) {
            cessAmount = assessableValue * (cessPercent / 100);
          }
        } else {
          // For other scenarios, CESS is calculated on taxable amount (FOB value)
          const itemTaxable = getItemTaxable(item);
          if (cessPercent > 0 && itemTaxable > 0) {
            cessAmount = itemTaxable * (cessPercent / 100);
          }
        }
      }
      
      return sum + cessAmount;
    }, 0);

    // Calculate BCD (Basic Customs Duty) for imports
    const basicCustomsDuty = lineItems.reduce((sum, item) => {
      return sum + parseFloat(item.bcd_amount || item.bcdAmount || 0);
    }, 0);

    // Calculate SWS (Social Welfare Surcharge) for imports
    const socialWelfareSurcharge = lineItems.reduce((sum, item) => {
      return sum + parseFloat(item.sws_amount || item.swsAmount || 0);
    }, 0);

    // Calculate Assessable Value for imports
    const assessableValue = lineItems.reduce((sum, item) => {
      return sum + parseFloat(item.assessable_value || item.assessableValue || 0);
    }, 0);

    // IGST on Assessable Value
    const igstOnAssessable = lineItems.reduce((sum, item) => {
      if (item.assessable_value || item.assessableValue) {
        return sum + parseFloat(item.igst_amount || item.igstAmount || 0);
      }
      return sum;
    }, 0);

    // Tax for third country
    const tax = lineItems.reduce((sum, item) => {
      return sum + parseFloat(item.tax_amount || item.taxAmount || 0);
    }, 0);

    // Calculate grand total
    let grandTotal = 0;
    if (tradeScenario === 'Third Country (Abroad → Abroad)') {
      grandTotal = taxableAmount + tax;
    } else if (tradeScenario === 'IN→ Export from India (India → Abroad)') {
      if (exportType === 'Non-LUT - IGST Applicable') {
        grandTotal = taxableAmount + igst + cess;
      } else {
        grandTotal = taxableAmount;
      }
    } else if (isInternationalTrade(tradeScenario) && tradeScenario.includes('Import')) {
      grandTotal = assessableValue + igstOnAssessable + cess;
    } else if (tradeScenario === 'IN Inter-State (Between Indian States)') {
      grandTotal = taxableAmount + igst + cess;
    } else {
      grandTotal = taxableAmount + cgst + sgst + cess;
    }

    return {
      subtotal,
      totalDiscount,
      taxableAmount,
      cgst,
      sgst,
      igst,
      cess,
      basicCustomsDuty,
      socialWelfareSurcharge,
      assessableValue,
      igstOnAssessable,
      tax,
      grandTotal
    };
  };

  const getStatusBadgeClass = (status) => {
    if (!status) return "brfq-status-badge brfq-status-pending";
    
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "accepted":
        return "brfq-status-badge brfq-status-accepted";
      case "rejected":
        return "brfq-status-badge brfq-status-rejected";
      case "draft":
        return "brfq-status-badge brfq-status-request"; // Draft uses request style
      case "submitted":
        return "brfq-status-badge brfq-status-accepted"; // Submitted uses accepted style
      case "pending approval":
        return "brfq-status-badge brfq-status-pending";
      case "payment done":
        return "brfq-status-badge brfq-status-accepted";
      case "payment pending":
        return "brfq-status-badge brfq-status-pending";
      case "awaiting payment":
        return "brfq-status-badge brfq-status-pending";
      case "paid":
        return "brfq-status-badge brfq-status-accepted";
      case "pending":
      case "created":
        return "brfq-status-badge brfq-status-pending";
      default:
        return "brfq-status-badge brfq-status-pending";
    }
  };

  const handleMarketplacePayment = (transformedOrder) => {
    const poInfo = transformedOrder.poInfo;
    if (!poInfo?.po_id) return;

    const currencyCode = transformedOrder.originalData?.currency || 'INR';
    const amount = transformedOrder.paymentAmountRaw || 0;
    const productLabel = transformedOrder.products || 'Marketplace product';

    navigate('/payment/gateway', {
      state: {
        paymentData: {
          context: 'marketplace_checkout',
          summaryTitle: `Marketplace payment · ${poInfo.po_number || `PO #${poInfo.po_id}`}`,
          summaryAmount: transformedOrder.totalAmount,
          summaryLines: [
            `Product: ${productLabel}`,
            `Supplier: ${transformedOrder.sellerName}`,
            `PO: ${poInfo.po_number || poInfo.po_id}`,
          ],
        },
        marketplacePoPayment: {
          purchaseOrderId: poInfo.po_id,
          poNumber: poInfo.po_number,
          amount,
          currency: currencyCode,
        },
        afterSuccessNavigate: '/purchase-order-list',
      },
    });
  };

  const handleViewDetails = async (poId) => {
    try {
      setLoadingPreview(true);
      setShowPreviewModal(true);
      
      // Fetch full PO details from buyer_purchase_orders table
      const poResponse = await CommonService.get(`/purchase-order/${poId}`);
      if (poResponse && poResponse.success && poResponse.data) {
        setPreviewPOData(poResponse.data);
      } else {
        toast.error('Failed to load purchase order details');
        setShowPreviewModal(false);
      }
    } catch (error) {
      console.error('Error fetching PO details:', error);
      toast.error('Failed to load purchase order details');
      setShowPreviewModal(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleViewAcceptedDetails = async (poId) => {
    try {
      setLoadingAcceptedDetails(true);
      setShowAcceptedDetailsModal(true);
      
      // Always fetch fresh data from buyer_purchase_orders table via API
      // This ensures we get all acceptance details from the database
      const poResponse = await CommonService.get(`/purchase-order/${poId}`);
      if (poResponse && poResponse.success && poResponse.data) {
        const poData = poResponse.data;
        
        // Extract seller accepted details from buyer_purchase_orders table
        // Use exact column names from buyer_purchase_orders table:
        // po_date, po_ack_number, estimated_dispatch_date, estimated_delivery_date_seller, acceptance_notes
        const acceptedDetails = {
          po_id: poData.id || poData.purchase_order_id,
          po_number: poData.po_number,
          // Use exact column names from buyer_purchase_orders table
          po_date: poData.po_date,
          po_ack_number: poData.po_ack_number,
          estimated_dispatch_date: poData.estimated_dispatch_date,
          estimated_delivery_date_seller: poData.estimated_delivery_date_seller,
          acceptance_notes: poData.acceptance_notes,
          // Acceptance date (for display - use accepted_at if available, otherwise po_date)
          accepted_at: poData.accepted_at || poData.accepted_date || poData.po_date || poData.updated_datetime,
          accepted_by: poData.accepted_by || poData.seller_company_name || poData.seller_name,
          // Status fields
          po_response_status: poData.po_response_status,
          po_status: poData.po_status,
          // Additional acceptance fields
          acceptance_attachment: poData.acceptance_attachment,
          terms_accepted: poData.terms_accepted,
          acknowledgement_reviewed: poData.acknowledgement_reviewed,
          // Include all other fields from the response
          ...poData
        };
        
        console.log('Accepted Details fetched from buyer_purchase_orders table:', acceptedDetails);
        console.log('Raw API response data:', poData);
        setAcceptedDetailsData(acceptedDetails);
      } else {
        toast.error('Failed to load accepted details');
        setShowAcceptedDetailsModal(false);
      }
    } catch (error) {
      console.error('Error fetching accepted details from buyer_purchase_orders table:', error);
      console.error('Error details:', error.response || error.message);
      toast.error('Failed to load accepted details');
      setShowAcceptedDetailsModal(false);
    } finally {
      setLoadingAcceptedDetails(false);
    }
  };

  const handleViewRejectedDetails = async (poId) => {
    try {
      setLoadingRejectedDetails(true);
      setShowRejectedDetailsModal(true);
      
      // Always fetch fresh data from buyer_purchase_orders table via API
      // This ensures we get all rejection details from the database
      const poResponse = await CommonService.get(`/purchase-order/${poId}`);
      if (poResponse && poResponse.success && poResponse.data) {
        const poData = poResponse.data;
        
        // Extract seller rejected details from buyer_purchase_orders table
        // Map all rejection-related fields from the database
        const rejectedDetails = {
          po_id: poData.id || poData.purchase_order_id,
          po_number: poData.po_number,
          // Rejection date fields
          rejected_at: poData.rejected_at || poData.rejected_date || poData.updated_datetime,
          rejected_by: poData.rejected_by || poData.seller_company_name || poData.seller_name,
          // Status fields
          po_response_status: poData.po_response_status || poData.po_status,
          po_status: poData.po_status,
          // Rejection information fields from buyer_purchase_orders table
          rejection_reason: poData.rejection_reason || null,
          rejection_details: poData.rejection_details || null,
          rejection_attachment: poData.rejection_attachment || null,
          // Include all other fields from the response
          ...poData
        };
        
        console.log('Rejected Details fetched from buyer_purchase_orders table:', rejectedDetails);
        console.log('Raw API response data:', poData);
        setRejectedDetailsData(rejectedDetails);
      } else {
        toast.error('Failed to load rejected details');
        setShowRejectedDetailsModal(false);
      }
    } catch (error) {
      console.error('Error fetching rejected details from buyer_purchase_orders table:', error);
      console.error('Error details:', error.response || error.message);
      toast.error('Failed to load rejected details');
      setShowRejectedDetailsModal(false);
    } finally {
      setLoadingRejectedDetails(false);
    }
  };

  const handleEdit = (poId) => {
    // For draft POs, navigate to po-creation page for editing
    navigate('/po-creation', { state: { poId, purchaseOrderId: poId } });
  };

  const handleCreatePO = (quotationId, quotationData) => {
    navigate('/po-creation', { 
      state: { 
        quotationId: quotationId,
        quotation: quotationData,
        fromPurchaseOrderList: true
      }
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const getCurrentOrders = () => {
    switch (activeTab) {
      case "create":
        return createOrders;
      case "accepted":
        return acceptedOrders;
      case "rejected":
        return rejectedOrders;
      default:
        return [];
    }
  };

  const getFilteredOrders = () => {
    const orders = getCurrentOrders();
    if (!searchQuery) return orders;
    
    const query = searchQuery.toLowerCase();
    return orders.filter(order => {
      // For quotations and draft POs (create tab)
      if (activeTab === 'create') {
        // Handle draft POs
        if (order.type === 'draft_po' && order.purchase_order_id) {
          return (
            (order.po_number && order.po_number.toLowerCase().includes(query)) ||
            (order.seller_company_name && order.seller_company_name.toLowerCase().includes(query)) ||
            (order.quotation_id && String(order.quotation_id).includes(query)) ||
            (order.rfq_id && String(order.rfq_id).includes(query))
          );
        }
        // Handle quotations
        const quotationId = order.quotation_number || (order.quotation_id ? `QUO-${order.quotation_id}` : '');
        const rfqId = order.rfq_id ? `REQ-${order.rfq_id}` : '';
        return (
          (quotationId && quotationId.toLowerCase().includes(query)) ||
          (rfqId && rfqId.toLowerCase().includes(query)) ||
          (order.seller_company_name && order.seller_company_name.toLowerCase().includes(query)) ||
          (order.seller_name && order.seller_name.toLowerCase().includes(query))
        );
      }
      // For purchase orders (draft/accepted/rejected tabs)
      return (
        (order.po_number && order.po_number.toLowerCase().includes(query)) ||
        (order.seller_company_name && order.seller_company_name && order.seller_company_name.toLowerCase().includes(query)) ||
        (order.seller_name && order.seller_name.toLowerCase().includes(query)) ||
        (order.quotation_id && String(order.quotation_id).includes(query))
      );
    });
  };

  const filteredOrders = getFilteredOrders();

  // Group orders/quotations by RFQ ID
  const groupOrdersByRFQ = (ordersList) => {
    const grouped = {};
    ordersList.forEach((order) => {
      // Get RFQ ID from order/quotation
      let rfqId = null;
      if (activeTab === 'create') {
        // Handle draft POs
        if (order.type === 'draft_po' && order.purchase_order_id) {
          // For draft POs, try to get RFQ ID from quotation_id
          if (order.rfq_id) {
            rfqId = `REQ-${order.rfq_id}`;
          } else if (order.quotation_id) {
            // Try to fetch RFQ ID from quotation if available
            rfqId = `QUO-${order.quotation_id}`;
          }
        } else if (order.rfq_id) {
          // Handle quotations
          rfqId = `REQ-${order.rfq_id}`;
        }
      } else if ((activeTab === 'accepted' || activeTab === 'rejected') && order.rfq_id) {
        rfqId = `REQ-${order.rfq_id}`;
      } else if ((activeTab === 'accepted' || activeTab === 'rejected') && order.quotation_id) {
        // Try to get RFQ ID from quotation if available
        // For now, we'll use quotation ID as fallback grouping
        rfqId = `QUO-${order.quotation_id}`;
      }
      
      // If no RFQ ID found, use a default group
      if (!rfqId) {
        rfqId = 'UNGROUPED';
      }
      
      if (!grouped[rfqId]) {
        grouped[rfqId] = [];
      }
      grouped[rfqId].push(order);
    });
    return grouped;
  };

  // Toggle RFQ expand/collapse
  const toggleRFQ = (rfqId) => {
    setExpandedRFQs((prev) => ({
      ...prev,
      [rfqId]: !prev[rfqId]
    }));
  };

  // Get grouped orders
  const groupedOrders = groupOrdersByRFQ(filteredOrders);
  const rfqIds = Object.keys(groupedOrders).sort();

  // Initialize all RFQs as collapsed by default when tab changes
  useEffect(() => {
    const allCollapsed = {};
    rfqIds.forEach((rfqId) => {
      allCollapsed[rfqId] = false;
    });
    setExpandedRFQs((prev) => ({ ...allCollapsed, ...prev }));
  }, [activeTab, rfqIds.length]);

  // Helper function to get unit label
  const getUnitLabel = (unitValue) => {
    if (!unitValue) return '';
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) return unitValue;
    
    const unitValueNum = isNaN(unitValue) ? null : Number(unitValue);
    const foundByValue = attributeUnitOptions.find(opt => {
      if (String(opt.value) === String(unitValue)) return true;
      if (unitValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === unitValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || unitValue;
    }
    
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === String(unitValue) || 
      opt.label?.toLowerCase() === String(unitValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return unitValue;
  };

  // Helper function to get currency symbol
  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return '';
    if (currencyOptions && currencyOptions.length > 0) {
      const foundByCode = currencyOptions.find(opt => 
        opt.currencyCode === String(currencyCode).toUpperCase() ||
        String(opt.value) === String(currencyCode) ||
        opt.currencyCode === String(currencyCode)
      );
      if (foundByCode && foundByCode.currencySymbol) {
        return foundByCode.currencySymbol;
      }
    }
    return '';
  };

  // Transform order/quotation data for display
  const transformOrderData = (order) => {
    // If it's a draft purchase order (for create tab)
    if (activeTab === 'create' && order.type === 'draft_po' && order.purchase_order_id) {
      // Parse PO data from comment field
      let poData = {};
      try {
        if (order.comment && typeof order.comment === 'string') {
          poData = JSON.parse(order.comment);
        } else if (order.po_data) {
          poData = order.po_data;
        }
      } catch (e) {
        console.warn('Error parsing PO data:', e);
      }
      
      const lineItems = poData.lineItems || [];
      const quotationCurrency = poData.currency || 'INR';
      const quotationCurrencyCode = getCurrencyLabel(quotationCurrency);
      
      // Calculate total amount from line items
      const totalAmount = lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.total) || 0);
      }, 0);
      
      // Determine status: Draft for draft POs
      const poStatus = (order.po_status || 'Draft').toString();
      const normalizedStatus = poStatus.charAt(0).toUpperCase() + poStatus.slice(1).toLowerCase();
      
      return {
        id: order.po_number || `PO-${order.purchase_order_id}`,
        po_id: order.purchase_order_id,
        status: normalizedStatus, // Will be 'Draft' for draft POs
        sellerName: order.seller_company_name || order.seller_name || 'Unknown Seller',
        quotationId: order.quotation_id ? `QUO-${order.quotation_id}` : 'N/A',
        rfqId: order.rfq_id ? `REQ-${order.rfq_id}` : 'N/A',
        poDate: formatDate(order.po_date),
        totalAmount: formatCurrency(totalAmount, quotationCurrencyCode),
        expectedDelivery: poData.expectedDeliveryDate ? formatDate(poData.expectedDeliveryDate) : 'N/A',
        isQuotation: false,
        originalData: order,
        // PO number to display
        poNumber: order.po_number || null
      };
    }
    
    // If it's a quotation (for create tab)
    if (activeTab === 'create' && order.quotation_id && order.type !== 'draft_po') {
      // Get RFQ data if available
      const rfq = order.rfqData || {};
      
      // Calculate quoted price (FOB value - before taxes) and total amount (including taxes)
      const lineItems = order.items || [];
      
      // Quoted Price = FOB Value (subtotal - discount) - BEFORE taxes
      const quotedPrice = lineItems.reduce((sum, item) => {
        const unitPrice = parseFloat(item.unit_price) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const discount = parseFloat(item.discount) || 0;
        const subtotal = unitPrice * quantity;
        const discountAmount = subtotal * (discount / 100);
        const fobValue = subtotal - discountAmount;
        return sum + fobValue;
      }, 0);
      
      // Total Amount = Sum of item.total (includes all taxes: CGST, SGST, IGST, CESS, etc.)
      const totalAmount = lineItems.reduce((sum, item) => {
        // Use item.total which already includes all taxes
        if (item.total !== undefined && item.total !== null) {
          return sum + (parseFloat(item.total) || 0);
        }
        
        // Fallback: Calculate total if item.total is not available
        const unitPrice = parseFloat(item.unit_price) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const discount = parseFloat(item.discount) || 0;
        const subtotal = unitPrice * quantity;
        const discountAmount = subtotal * (discount / 100);
        const taxableAmount = subtotal - discountAmount;
        
        // Calculate taxes
        const cgstAmount = parseFloat(item.cgst_amount) || 0;
        const sgstAmount = parseFloat(item.sgst_amount) || 0;
        const igstAmount = parseFloat(item.igst_amount) || 0;
        const cessAmount = parseFloat(item.cess_amount) || 0;
        const customsAmount = parseFloat(item.customs_amount) || 0;
        const bcdAmount = parseFloat(item.bcd_amount) || 0;
        const swsAmount = parseFloat(item.sws_amount) || 0;
        const taxAmount = parseFloat(item.tax_amount) || 0;
        
        // Calculate total based on what taxes are present
        // For international trade, use assessable_value if available
        if (item.assessable_value !== undefined && item.assessable_value !== null) {
          const assessableValue = parseFloat(item.assessable_value) || 0;
          const igstOnAssessable = parseFloat(item.igst_amount) || 0;
          const cessOnAssessable = parseFloat(item.cess_amount) || 0;
          return sum + (assessableValue + igstOnAssessable + cessOnAssessable);
        }
        
        // For regular trade, add all applicable taxes
        return sum + (taxableAmount + cgstAmount + sgstAmount + igstAmount + cessAmount + customsAmount + bcdAmount + swsAmount + taxAmount);
      }, 0);
      
      const quotationCurrency = order.currency || 'INR';
      const quotationCurrencyCode = getCurrencyLabel(quotationCurrency);
      const currencySymbol = getCurrencySymbol(quotationCurrencyCode);
      
      // Format target price range from RFQ
      const priceRange = rfq.target_price_min && rfq.target_price_max 
        ? `${currencySymbol}${rfq.target_price_min} - ${currencySymbol}${rfq.target_price_max}` 
        : rfq.target_price_currency ? 'Price on request' : 'N/A';
      
      // Get quantity with unit from RFQ
      const quantityDisplay = rfq.product_quantity 
        ? `${rfq.product_quantity || 0} ${getUnitLabel(rfq.quantity_unit || '')}`.trim()
        : 'N/A';
      
      // Get PO information if it exists
      const poInfo = order.poInfo || null;
      const isMarketplaceDirect = resolveMarketplaceDirectOrder(order, poInfo);
      const paymentStatus = isMarketplaceDirect && poInfo?.po_id
        ? resolveMarketplacePaymentStatus(poInfo)
        : null;
      
      // Determine status based on PO state:
      // Submitted → if PO is created and submitted
      // Draft → if PO is saved as draft
      // Pending → if PO is not yet created for that quotation
      // Marketplace direct orders → payment status only (no seller approval)
      let status = 'Pending';
      if (isMarketplaceDirect && poInfo?.po_id) {
        status = paymentStatus === 'paid' ? 'Payment Done' : 'Payment Pending';
      } else if (poInfo && poInfo.po_status) {
        const poStatus = (poInfo.po_status || '').toString().toLowerCase();
        if (poStatus === 'submitted') {
          status = 'Submitted';
        } else if (poStatus === 'draft') {
          status = 'Draft';
        } else if (poStatus === 'rejected' || poStatus === 'accepted') {
          // Don't show rejected or accepted status in Create PO tab
          // If PO is rejected or accepted, still show as Pending (or the original quotation status)
          status = 'Pending';
        } else {
          // For any other PO status, default to the PO status (capitalized)
          status = poInfo.po_status.charAt(0).toUpperCase() + poInfo.po_status.slice(1).toLowerCase();
        }
      } else {
        status = 'Pending'; // No PO created yet
      }
      
      return {
        id: order.quotation_number || `QUO-${order.quotation_id}`,
        po_id: order.quotation_id,
        status: status,
        sellerName: order.seller_company_name || order.seller_name || 'Unknown Seller',
        quotationId: order.quotation_number || `QUO-${order.quotation_id}`,
        rfqId: order.rfq_id ? `REQ-${order.rfq_id}` : 'N/A',
        poDate: formatDate(order.quote_date || order.created_at),
        totalAmount: formatCurrency(totalAmount, quotationCurrencyCode),
        quotedPrice: formatCurrency(quotedPrice, quotationCurrencyCode),
        expectedPrice: priceRange,
        expectedDelivery: order.validity_days ? (() => {
          const quoteDate = order.quote_date ? new Date(order.quote_date) : new Date(order.created_at);
          quoteDate.setDate(quoteDate.getDate() + parseInt(order.validity_days));
          return formatDate(quoteDate);
        })() : 'N/A',
        // RFQ-based fields
        products: rfq.product_name || 'N/A',
        category: rfq.category_name || 'N/A',
        quantity: quantityDisplay,
        isQuotation: true,
        originalData: order,
        rfqData: rfq,
        // PO information if exists
        poInfo: poInfo,
        isMarketplaceDirect,
        paymentStatus,
        paymentAmountRaw: totalAmount,
        // PO number to display if PO exists
        poNumber: poInfo?.po_number || null,
        // PO ID for editing if PO is draft
        existingPoId: (poInfo?.po_status?.toLowerCase() === 'draft' && poInfo?.po_id) ? poInfo.po_id : null
      };
    }
    
    // If it's a purchase order (for accepted/rejected tabs)
    // Parse PO data from comment field for total amount calculation
    let poData = {};
    let totalAmount = order.total_amount || 0;
    try {
      // First, try to use line_items from full PO details (if available)
      if (order.line_items && Array.isArray(order.line_items) && order.line_items.length > 0) {
        totalAmount = order.line_items.reduce((sum, item) => {
          return sum + (parseFloat(item.total) || 0);
        }, 0);
        // Also set poData.lineItems for consistency
        poData = { ...poData, lineItems: order.line_items };
      } else if (order.comment && typeof order.comment === 'string') {
        poData = JSON.parse(order.comment);
        if (poData.lineItems && poData.lineItems.length > 0) {
          totalAmount = poData.lineItems.reduce((sum, item) => {
            return sum + (parseFloat(item.total) || 0);
          }, 0);
        }
      } else if (order.po_data) {
        poData = order.po_data;
        if (poData.lineItems && poData.lineItems.length > 0) {
          totalAmount = poData.lineItems.reduce((sum, item) => {
            return sum + (parseFloat(item.total) || 0);
          }, 0);
        }
      }
    } catch (e) {
      console.warn('Error parsing PO data:', e);
    }
    
    const currency = poData.currency || order.currency || 'INR';
    const currencyCode = getCurrencyLabel(currency);
    
    // Determine status for purchase orders
    let status = 'Pending';
    if (order.po_status) {
      status = order.po_status; // Will be 'Draft' or 'Submitted'
    } else if (order.po_response_status) {
      status = order.po_response_status; // For accepted/rejected tabs
    }
    
    // Extract product information - prioritize RFQ data (rfq_request table) for product name
    // Prioritize order.line_items (from full PO details) over poData.lineItems for quantity
    const lineItems = order.line_items || poData.lineItems || [];
    let productNames = [];
    let productCategories = [];
    let totalQuantity = 0;
    let quantityUnit = '';
    
    // First, try to get product name from RFQ request table (rfq_data)
    if (order.rfq_data && order.rfq_data.product_name) {
      const rfqProductName = order.rfq_data.product_name.trim();
      if (rfqProductName && rfqProductName !== '' && rfqProductName !== 'N/A') {
        productNames.push(rfqProductName);
      }
      
      // Also get category from RFQ if available
      if (order.rfq_data.category_name) {
        productCategories.push(order.rfq_data.category_name.trim());
      }
    }
    
    // If no product name from RFQ, fallback to line items
    if (productNames.length === 0 && lineItems && lineItems.length > 0) {
      // Extract product names from line items
      lineItems.forEach(item => {
        let productName = item.description || item.productName || item.product_name || item.name || '';
        
        // Remove HSN code from product name if present
        // HSN codes are typically 4, 6, or 8 digit numbers
        // Remove patterns like "HSN: 12345678", "HSN-12345678", or just numeric codes at the end
        if (productName) {
          // Remove HSN code patterns (case insensitive)
          productName = productName
            .replace(/\bHSN\s*[:\-]?\s*\d{4,8}\b/gi, '') // Remove "HSN: 12345678" or "HSN-12345678"
            .replace(/\b\d{4,8}\s*\(HSN\)/gi, '') // Remove "12345678 (HSN)"
            .replace(/\bHSN\s*Code\s*[:\-]?\s*\d{4,8}\b/gi, '') // Remove "HSN Code: 12345678"
            .trim();
          
          // Clean up any extra spaces or separators left behind
          productName = productName.replace(/\s+/g, ' ').trim();
        }
        
        if (productName && productName.trim() !== '' && productName !== 'N/A') {
          if (!productNames.includes(productName.trim())) {
            productNames.push(productName.trim());
          }
        }
        
        // Extract category if available (only if not already from RFQ)
        if (productCategories.length === 0) {
          const category = item.category || item.productCategory || item.product_category || item.category_name || '';
          if (category && category.trim() !== '' && !productCategories.includes(category.trim())) {
            productCategories.push(category.trim());
          }
        }
      });
    }
    
    // Calculate quantity from line items
    if (lineItems && lineItems.length > 0) {
      lineItems.forEach(item => {
        // Calculate total quantity
        const itemQuantity = parseFloat(item.quantity) || 0;
        totalQuantity += itemQuantity;
        
        // Get unit from first item
        if (!quantityUnit && item.uom) {
          quantityUnit = getUnitLabel(item.uom);
        }
      });
    }
    
    // If no product name from RFQ or line items, try to get from quotation if available
    if (productNames.length === 0 && order.quotation_id) {
      // Try to fetch quotation data if not already available
      // For now, we'll use what's available in the order data
      if (order.quotation_data) {
        const quotation = order.quotation_data;
        if (quotation.items && quotation.items.length > 0) {
          quotation.items.forEach(item => {
            let productName = item.description || item.productName || '';
            
            // Remove HSN code from product name if present
            if (productName) {
              productName = productName
                .replace(/\bHSN\s*[:\-]?\s*\d{4,8}\b/gi, '') // Remove "HSN: 12345678" or "HSN-12345678"
                .replace(/\b\d{4,8}\s*\(HSN\)/gi, '') // Remove "12345678 (HSN)"
                .replace(/\bHSN\s*Code\s*[:\-]?\s*\d{4,8}\b/gi, '') // Remove "HSN Code: 12345678"
                .trim();
              
              // Clean up any extra spaces or separators left behind
              productName = productName.replace(/\s+/g, ' ').trim();
            }
            
            if (productName && !productNames.includes(productName.trim())) {
              productNames.push(productName.trim());
            }
          });
        }
      }
    }
    
    const productsDisplay = productNames.length > 0 
      ? (productNames.length > 3 ? productNames.slice(0, 3).join(', ') + ` +${productNames.length - 3} more` : productNames.join(', '))
      : 'N/A';
    
    const categoryDisplay = productCategories.length > 0 
      ? productCategories.join(', ')
      : 'N/A';
    
    const quantityDisplay = totalQuantity > 0 
      ? `${totalQuantity} ${quantityUnit || ''}`.trim()
      : 'N/A';
    
    // Extract payment terms
    const paymentTerms = order.payment_terms || poData.paymentTerms || poData.payment_terms || 'N/A';
    
    return {
      id: order.po_number || `PO-${order.purchase_order_id}`,
      po_id: order.purchase_order_id,
      status: status,
      sellerName: order.seller_company_name || order.seller_name || 'Unknown Seller',
      quotationId: order.quotation_id ? `QUO-${order.quotation_id}` : 'N/A',
      quotation_id: order.quotation_id, // Store raw quotation_id for creating new PO
      poDate: formatDate(order.po_date),
      totalAmount: formatCurrency(totalAmount, currencyCode),
      expectedDelivery: poData.expectedDeliveryDate ? formatDate(poData.expectedDeliveryDate) : (order.expected_delivery_date ? formatDate(order.expected_delivery_date) : 'N/A'),
      acceptedOn: order.accepted_at ? formatDate(order.accepted_at) : (order.updated_datetime && order.po_response_status?.toLowerCase() === 'accepted' ? formatDate(order.updated_datetime) : null),
      rejectedOn: order.rejected_at ? formatDate(order.rejected_at) : (order.updated_datetime && order.po_response_status?.toLowerCase() === 'rejected' ? formatDate(order.updated_datetime) : null),
      isQuotation: false,
      originalData: order,
      // Product information for accepted tab
      products: productsDisplay,
      category: categoryDisplay,
      quantity: quantityDisplay,
      paymentTerms: paymentTerms
    };
  };

  // Helper function to get currency label
  const getCurrencyLabel = (currencyValue) => {
    if (!currencyValue) return 'INR';
    if (!currencyOptions || currencyOptions.length === 0) return currencyValue;
    
    const foundByValue = currencyOptions.find(opt => 
      String(opt.value) === String(currencyValue) || 
      opt.currencyCode === String(currencyValue)
    );
    if (foundByValue) {
      return foundByValue.currencyCode || foundByValue.label || currencyValue;
    }
    
    const foundByCode = currencyOptions.find(opt => 
      opt.currencyCode === String(currencyValue).toUpperCase()
    );
    if (foundByCode) {
      return foundByCode.currencyCode || foundByCode.label || currencyValue;
    }
    
    return currencyValue;
  };

  // Render order card content
  const renderOrderCard = (transformedOrder, order) => {
    return (
      <div className="brfq-card-content">
        <div className="brfq-card-header">
          <div className="brfq-card-header-left">
            <span className="brfq-rfq-id">{transformedOrder.id}</span>
            {activeTab === 'create' && transformedOrder.isQuotation && transformedOrder.poInfo && transformedOrder.poInfo.po_number && (
              <span className="brfq-rfq-id" style={{ marginLeft: '8px', color: '#666', fontSize: '0.9em' }}>
                PO: {transformedOrder.poInfo.po_number}
              </span>
            )}
            <span className={getStatusBadgeClass(transformedOrder.status)}>
              {transformedOrder.status ? transformedOrder.status.charAt(0).toUpperCase() + transformedOrder.status.slice(1).toLowerCase() : 'Pending'}
            </span>
          </div>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className="brfq-chevron-icon"
          >
            <path d="m9 18 6-6-6-6"></path>
          </svg>
        </div>

        <div className="brfq-buyer-row">
          <div className="brfq-buyer-info">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="14" 
              height="14" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className="brfq-user-icon"
            >
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            <span className="brfq-buyer-label">Seller</span>
            <span className="brfq-buyer-name">{transformedOrder.sellerName}</span>
          </div>
          {activeTab === 'create' && (
            <div className="brfq-action-buttons">
              {transformedOrder.isQuotation && (
                <>
                  {(() => {
                    if (transformedOrder.isMarketplaceDirect && transformedOrder.poInfo?.po_id) {
                      if (transformedOrder.paymentStatus === 'pending') {
                        return (
                          <button
                            className="brfq-btn-submit"
                            onClick={() => handleMarketplacePayment(transformedOrder)}
                          >
                            Complete Payment
                          </button>
                        );
                      }
                      return (
                        <button
                          className="brfq-btn-clarification"
                          onClick={() => handleViewDetails(transformedOrder.poInfo.po_id)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="brfq-chat-icon"
                          >
                            <path d="M3 3v18h18"></path>
                            <path d="M18 7l-5-5-5 5"></path>
                            <path d="M7 12h10"></path>
                          </svg>
                          View PO
                        </button>
                      );
                    }

                    const poStatus = (transformedOrder.poInfo?.po_status || '').toString().toLowerCase();
                    if (poStatus === 'draft') {
                      return (
                        <button 
                          className="brfq-btn-clarification"
                          onClick={() => handleEdit(transformedOrder.poInfo.po_id)}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            className="brfq-chat-icon"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                          Edit PO
                        </button>
                      );
                    }
                    if (poStatus === 'submitted') {
                      return (
                        <button 
                          className="brfq-btn-clarification"
                          onClick={() => handleViewDetails(transformedOrder.poInfo.po_id)}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            className="brfq-chat-icon"
                          >
                            <path d="M3 3v18h18"></path>
                            <path d="M18 7l-5-5-5 5"></path>
                            <path d="M7 12h10"></path>
                          </svg>
                          Track PO
                        </button>
                      );
                    }
                    if (poStatus === 'pending approval' && transformedOrder.poInfo?.po_id) {
                      return (
                        <button 
                          className="brfq-btn-clarification"
                          onClick={() => handleViewDetails(transformedOrder.poInfo.po_id)}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            className="brfq-chat-icon"
                          >
                            <path d="M3 3v18h18"></path>
                            <path d="M18 7l-5-5-5 5"></path>
                            <path d="M7 12h10"></path>
                          </svg>
                          View PO
                        </button>
                      );
                    }
                    if (!transformedOrder.poInfo?.po_id) {
                      return (
                        <button 
                          className="brfq-btn-submit"
                          onClick={() => handleCreatePO(transformedOrder.po_id, transformedOrder.originalData)}
                        >
                          Create PO
                        </button>
                      );
                    }
                    return null;
                  })()}
                  {(() => {
                    const poStatus = (transformedOrder.poInfo?.po_status || '').toString().toLowerCase();
                    // Marketplace checkout orders skip seller approval — no logistics until paid
                    if (transformedOrder.isMarketplaceDirect) return null;
                    // Logistics only after PO is submitted to seller (not draft or pending approval)
                    if (poStatus === 'draft' || poStatus === 'pending approval') return null;
                    // "Start Logistics Flow" — visible in Create PO tab once the
                    // commercial side is confirmed (PO created OR quotation
                    // accepted). Prefer the PO id when available so the generic
                    // logistics form receives the richer prefill (PO number,
                    // delivery date, seller address, etc.); otherwise fall back
                    // to the accepted quotation as the source.
                    const poId = transformedOrder.poInfo?.po_id ?? null;
                    const quotationId = transformedOrder.po_id ?? null; // (this field actually holds quotation_id)
                    const target = poId
                      ? `/logistics/rfq/generic?salesOrderId=${encodeURIComponent(String(poId))}`
                      : quotationId
                        ? `/logistics/rfq/generic?sellerQuoteId=${encodeURIComponent(String(quotationId))}`
                        : null;
                    if (!target) return null;
                    return (
                      <button
                        className="brfq-btn-clarification"
                        onClick={() => navigate(target)}
                        title="Start the logistics RFQ for this order"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="brfq-chat-icon"
                        >
                          <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path>
                          <path d="M15 18H9"></path>
                          <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"></path>
                          <circle cx="17" cy="18" r="2"></circle>
                          <circle cx="7" cy="18" r="2"></circle>
                        </svg>
                        Start Logistics Flow
                      </button>
                    );
                  })()}
                </>
              )}
            </div>
          )}
          {(activeTab === 'accepted' || activeTab === 'rejected') && (
            <div className="brfq-action-buttons">
              {activeTab === 'accepted' && (
                <>
                  <button 
                    className="brfq-btn-clarification"
                    onClick={() => handleViewDetails(transformedOrder.po_id)}
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="brfq-chat-icon"
                    >
                      <path d="M3 3v18h18"></path>
                      <path d="M18 7l-5-5-5 5"></path>
                      <path d="M7 12h10"></path>
                    </svg>
                    Track PO
                  </button>
                  <button 
                    className="brfq-btn-clarification"
                    onClick={() => handleViewAcceptedDetails(transformedOrder.po_id)}
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="brfq-chat-icon"
                    >
                      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                      <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                      <path d="M10 9H8"></path>
                      <path d="M16 13H8"></path>
                      <path d="M16 17H8"></path>
                    </svg>
                    View Acceptance Details
                  </button>
                </>
              )}
              {activeTab === 'rejected' && (
                <>
                  <button 
                    className="brfq-btn-clarification"
                    onClick={() => handleViewDetails(transformedOrder.po_id)}
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="brfq-chat-icon"
                    >
                      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                      <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                      <path d="M10 9H8"></path>
                      <path d="M16 13H8"></path>
                      <path d="M16 17H8"></path>
                    </svg>
                    View PO Details
                  </button>
                  <button 
                    className="brfq-btn-clarification"
                    onClick={() => handleViewRejectedDetails(transformedOrder.po_id)}
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="brfq-chat-icon"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    View Reject Details
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className={`brfq-details-grid ${activeTab === 'create' ? 'brfq-details-grid-quoted' : ''} ${activeTab === 'accepted' ? 'brfq-details-grid-accepted' : ''} ${activeTab === 'rejected' ? 'brfq-details-grid-rejected' : ''}`}>
          {activeTab === 'create' && transformedOrder.isQuotation && (
            <>
              <div className="brfq-detail-item">
                <div className="brfq-detail-label">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className="brfq-detail-icon"
                  >
                    <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                    <path d="M12 22V12"></path>
                    <polyline points="3.29 7 12 12 20.71 7"></polyline>
                    <path d="m7.5 4.27 9 5.15"></path>
                  </svg>
                  Products
                </div>
                <div className="brfq-detail-value">{transformedOrder.products}</div>
              </div>

              <div className="brfq-detail-item">
                <div className="brfq-detail-label">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className="brfq-detail-icon"
                  >
                    <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                    <path d="M12 22V12"></path>
                    <polyline points="3.29 7 12 12 20.71 7"></polyline>
                    <path d="m7.5 4.27 9 5.15"></path>
                  </svg>
                  Category
                </div>
                <div className="brfq-detail-value">{transformedOrder.category}</div>
              </div>

              <div className="brfq-detail-item">
                <div className="brfq-detail-label">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className="brfq-detail-icon"
                  >
                    <line x1="4" x2="20" y1="9" y2="9"></line>
                    <line x1="4" x2="20" y1="15" y2="15"></line>
                    <line x1="10" x2="8" y1="3" y2="21"></line>
                    <line x1="16" x2="14" y1="3" y2="21"></line>
                  </svg>
                  Quantity
                </div>
                <div className="brfq-detail-value">{transformedOrder.quantity}</div>
              </div>

              {transformedOrder.quotedPrice && (
                <div className="brfq-detail-item">
                  <div className="brfq-detail-label">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="brfq-detail-icon"
                    >
                      <line x1="12" x2="12" y1="2" y2="22"></line>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                    </svg>
                    Quoted Price
                  </div>
                  <div className="brfq-detail-value brfq-price-value brfq-quoted-price-value">{transformedOrder.quotedPrice}</div>
                </div>
              )}
            </>
          )}
          
          {activeTab === 'create' && transformedOrder.isQuotation && (
            <>
              <div className="brfq-detail-item">
                <div className="brfq-detail-label">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className="brfq-detail-icon"
                  >
                    <path d="M8 2v4"></path>
                    <path d="M16 2v4"></path>
                    <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                    <path d="M3 10h18"></path>
                  </svg>
                  Quote Date
                </div>
                <div className="brfq-detail-value">{transformedOrder.poDate}</div>
              </div>
            </>
          )}
          
          {!(activeTab === 'create' && transformedOrder.isQuotation) && (
            <div className="brfq-detail-item">
              <div className="brfq-detail-label">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className="brfq-detail-icon"
                >
                  <path d="M8 2v4"></path>
                  <path d="M16 2v4"></path>
                  <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                  <path d="M3 10h18"></path>
                </svg>
                PO Date
              </div>
              <div className="brfq-detail-value">{transformedOrder.poDate}</div>
            </div>
          )}

          <div className="brfq-detail-item">
            <div className="brfq-detail-label">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="14" 
                height="14" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className="brfq-detail-icon"
              >
                <line x1="12" x2="12" y1="2" y2="22"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              Total Amount
            </div>
            <div className="brfq-detail-value brfq-price-value">{transformedOrder.totalAmount}</div>
          </div>

          {activeTab === 'accepted' && (
            <>
              {transformedOrder.products && transformedOrder.products !== 'N/A' && (
                <div className="brfq-detail-item">
                  <div className="brfq-detail-label">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="brfq-detail-icon"
                    >
                      <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                      <path d="M12 22V12"></path>
                      <polyline points="3.29 7 12 12 20.71 7"></polyline>
                      <path d="m7.5 4.27 9 5.15"></path>
                    </svg>
                    Product Name
                  </div>
                  <div className="brfq-detail-value" title={transformedOrder.products}>{transformedOrder.products}</div>
                </div>
              )}
              {transformedOrder.category && transformedOrder.category !== 'N/A' && (
                <div className="brfq-detail-item">
                  <div className="brfq-detail-label">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="brfq-detail-icon"
                    >
                      <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                      <path d="M12 22V12"></path>
                      <polyline points="3.29 7 12 12 20.71 7"></polyline>
                      <path d="m7.5 4.27 9 5.15"></path>
                    </svg>
                    Category
                  </div>
                  <div className="brfq-detail-value">{transformedOrder.category}</div>
                </div>
              )}
              {transformedOrder.quantity && transformedOrder.quantity !== 'N/A' && (
                <div className="brfq-detail-item">
                  <div className="brfq-detail-label">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="brfq-detail-icon"
                    >
                      <line x1="4" x2="20" y1="9" y2="9"></line>
                      <line x1="4" x2="20" y1="15" y2="15"></line>
                      <line x1="10" x2="8" y1="3" y2="21"></line>
                      <line x1="16" x2="14" y1="3" y2="21"></line>
                    </svg>
                    Quantity
                  </div>
                  <div className="brfq-detail-value">{transformedOrder.quantity}</div>
                </div>
              )}
              {transformedOrder.acceptedOn && (
                <div className="brfq-detail-item">
                  <div className="brfq-detail-label">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="brfq-detail-icon"
                    >
                      <path d="M8 2v4"></path>
                      <path d="M16 2v4"></path>
                      <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                      <path d="M3 10h18"></path>
                    </svg>
                    Accepted On
                  </div>
                  <div className="brfq-detail-value">{transformedOrder.acceptedOn}</div>
                </div>
              )}
            </>
          )}
          {activeTab === 'rejected' && (
            <>
              {transformedOrder.products && transformedOrder.products !== 'N/A' && (
                <div className="brfq-detail-item">
                  <div className="brfq-detail-label">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="brfq-detail-icon"
                    >
                      <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                      <path d="M12 22V12"></path>
                      <polyline points="3.29 7 12 12 20.71 7"></polyline>
                      <path d="m7.5 4.27 9 5.15"></path>
                    </svg>
                    Product Name
                  </div>
                  <div className="brfq-detail-value" title={transformedOrder.products}>{transformedOrder.products}</div>
                </div>
              )}
              {transformedOrder.category && transformedOrder.category !== 'N/A' && (
                <div className="brfq-detail-item">
                  <div className="brfq-detail-label">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="brfq-detail-icon"
                    >
                      <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                      <path d="M12 22V12"></path>
                      <polyline points="3.29 7 12 12 20.71 7"></polyline>
                      <path d="m7.5 4.27 9 5.15"></path>
                    </svg>
                    Category
                  </div>
                  <div className="brfq-detail-value">{transformedOrder.category}</div>
                </div>
              )}
              {transformedOrder.quantity && transformedOrder.quantity !== 'N/A' && (
                <div className="brfq-detail-item">
                  <div className="brfq-detail-label">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="brfq-detail-icon"
                    >
                      <line x1="4" x2="20" y1="9" y2="9"></line>
                      <line x1="4" x2="20" y1="15" y2="15"></line>
                      <line x1="10" x2="8" y1="3" y2="21"></line>
                      <line x1="16" x2="14" y1="3" y2="21"></line>
                    </svg>
                    Quantity
                  </div>
                  <div className="brfq-detail-value">{transformedOrder.quantity}</div>
                </div>
              )}
              {transformedOrder.rejectedOn && (
                <div className="brfq-detail-item">
                  <div className="brfq-detail-label">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="brfq-detail-icon"
                    >
                      <path d="M8 2v4"></path>
                      <path d="M16 2v4"></path>
                      <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                      <path d="M3 10h18"></path>
                    </svg>
                    Rejected On
                  </div>
                  <div className="brfq-detail-value">{transformedOrder.rejectedOn}</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="buyer-rfq-list">
      <div className="brfq-main-content">
        <div className="brfq-title-section">
          <h1 className="brfq-main-title">Purchase Order List</h1>
          <p className="brfq-subtitle">Manage your purchase orders and track their status.</p>
        </div>

        {/* Search Bar */}
        <div className="brfq-search-section">
          <div className="brfq-search-wrapper">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className="brfq-search-icon"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              className="brfq-search-input"
              placeholder={
                activeTab === 'create' 
                  ? "Search by quotation ID, RFQ ID, or seller name..." 
                  : "Search by PO number, seller name, or quotation ID..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="brfq-tabs">
          <button
            className={`brfq-tab ${activeTab === 'create' ? 'brfq-tab-active' : ''}`}
            onClick={() => handleTabChange('create')}
          >
            Create Order ({createOrders.length})
          </button>
          <button
            className={`brfq-tab ${activeTab === 'accepted' ? 'brfq-tab-active' : ''}`}
            onClick={() => handleTabChange('accepted')}
          >
            Accepted Order ({acceptedOrders.length})
          </button>
          <button
            className={`brfq-tab ${activeTab === 'rejected' ? 'brfq-tab-active' : ''}`}
            onClick={() => handleTabChange('rejected')}
          >
            Rejected Order ({rejectedOrders.length})
          </button>
        </div>

        {/* PO Cards - Grouped by RFQ */}
        <div className="brfq-cards-container">
          {loading && (activeTab === 'create' || activeTab === 'accepted' || activeTab === 'rejected') ? (
            <div className="pol-loading-container">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="pol-loading-text">Loading purchase orders...</p>
            </div>
          ) : rfqIds.length > 0 ? (
            rfqIds.map((rfqId) => {
              const rfqOrders = groupedOrders[rfqId];
              const ordersCount = rfqOrders.length;
              const isExpanded = expandedRFQs[rfqId] === true;
              
              // Skip UNGROUPED header if it's the only group or if there are other groups
              if (rfqId === 'UNGROUPED' && rfqIds.length > 1) {
                // Render ungrouped items directly without header
                return rfqOrders.map((order) => {
                  const transformedOrder = transformOrderData(order);
                  return (
                    <div key={transformedOrder.po_id} className="brfq-card">
                      {renderOrderCard(transformedOrder, order)}
                    </div>
                  );
                });
              }
              
              return (
                <div key={rfqId} className="sql-rfq-group">
                  <div 
                    className="sql-rfq-header" 
                    onClick={() => toggleRFQ(rfqId)}
                  >
                    <div className="sql-rfq-header-left">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        className="sql-rfq-icon"
                        aria-hidden="true"
                      >
                        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                        <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                        <path d="M10 9H8"></path>
                        <path d="M16 13H8"></path>
                        <path d="M16 17H8"></path>
                      </svg>
                      <div className="sql-rfq-info">
                        <div className="sql-rfq-number">{rfqId}</div>
                        <div className="sql-rfq-suppliers-count">
                          {ordersCount} {ordersCount === 1 ? 'order' : 'orders'}
                        </div>
                      </div>
                    </div>
                    <div className="sql-rfq-header-right">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        className="sql-rfq-chevron"
                        aria-hidden="true"
                        style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s ease' }}
                      >
                        <path d="m18 15-6-6-6 6"></path>
                      </svg>
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div className="sql-rfq-quotations">
                      {rfqOrders.map((order) => {
                        const transformedOrder = transformOrderData(order);
                        return (
                          <div key={transformedOrder.po_id} className="brfq-card">
                            {renderOrderCard(transformedOrder, order)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="brfq-empty-state">
              <p>
                {activeTab === "create" && "No accepted quotations available."}
                {activeTab === "accepted" && "No submitted purchase orders."}
                {activeTab === "rejected" && "No rejected purchase orders."}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && previewPOData && (
        <div className="po-preview-modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="po-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="po-preview-modal-header">
              <div className="po-preview-modal-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-preview-icon">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <div>
                  <h2 className="po-preview-modal-title">Purchase Order Details</h2>
                  <p className="po-preview-modal-subtitle">View complete purchase order information</p>
                </div>
              </div>
              <button className="po-preview-modal-close" onClick={() => setShowPreviewModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="po-preview-modal-body">
              {loadingPreview ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p style={{ marginTop: '16px', color: '#666' }}>Loading purchase order details...</p>
                </div>
              ) : (
                <>
                  {/* Reference Quotation */}
                  {previewPOData.quotation_id && (
                    <div className="po-preview-section">
                      <h3 className="po-preview-section-title">Reference Quotation</h3>
                      <div className="po-preview-grid">
                        <div className="po-preview-item">
                          <label>Quotation ID</label>
                          <p>{previewPOData.quotation_id ? `QUO-${previewPOData.quotation_id}` : 'N/A'}</p>
                        </div>
                        {previewPOData.rfq_id && (
                          <div className="po-preview-item">
                            <label>Linked RFQ</label>
                            <p>{previewPOData.rfq_id ? `REQ-${previewPOData.rfq_id}` : 'N/A'}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* PO Header Info */}
                  <div className="po-preview-section">
                    <h3 className="po-preview-section-title">Purchase Order Details</h3>
                    <div className="po-preview-grid">
                      <div className="po-preview-item">
                        <label>PO Number</label>
                        <p>{previewPOData.po_number || 'N/A'}</p>
                      </div>
                      <div className="po-preview-item">
                        <label>PO Date</label>
                        <p>{formatDateForPreview(previewPOData.po_date)}</p>
                      </div>
                      <div className="po-preview-item">
                        <label>Expected Delivery Date</label>
                        <p>{formatDateForPreview(previewPOData.expected_delivery_date || previewPOData.po_data?.expectedDeliveryDate)}</p>
                      </div>
                      <div className="po-preview-item">
                        <label>Priority Level</label>
                        <p>{previewPOData.priority_level || previewPOData.po_data?.priorityLevel || 'Normal'}</p>
                      </div>
                      <div className="po-preview-item">
                        <label>Payment Terms</label>
                        <p>{previewPOData.payment_terms || previewPOData.po_data?.paymentTerms || 'N/A'}</p>
                      </div>
                      <div className="po-preview-item">
                        <label>Currency</label>
                        <p>{(() => {
                          const currency = previewPOData.currency || previewPOData.po_data?.currency || 'INR';
                          if (currencyOptions && currencyOptions.length > 0) {
                            const found = currencyOptions.find(opt => 
                              opt.currencyCode === currency || opt.value === currency
                            );
                            return found ? found.label : currency;
                          }
                          return currency;
                        })()}</p>
                      </div>
                      <div className="po-preview-item">
                        <label>Freight Terms</label>
                        <p>{previewPOData.freight_terms || previewPOData.po_data?.freightTerms || previewPOData.incoterms || 'N/A'}</p>
                      </div>
                      <div className="po-preview-item">
                        <label>Delivery Terms</label>
                        <p>{previewPOData.delivery_terms || previewPOData.po_data?.deliveryTerms || 'N/A'}</p>
                      </div>
                      <div className="po-preview-item">
                        <label>Trade Scenario</label>
                        <p>{previewPOData.trade_scenario || previewPOData.po_data?.tradeScenario || 'N/A'}</p>
                      </div>
                      <div className="po-preview-item">
                        <label>PO Status</label>
                        <p>{previewPOData.po_status || 'N/A'}</p>
                      </div>
                      <div className="po-preview-item">
                        <label>PO Response Status</label>
                        <p>{previewPOData.po_response_status || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* International Trade Details */}
                  {isInternationalTrade(previewPOData.trade_scenario || previewPOData.po_data?.tradeScenario) && (
                    <div className="po-preview-section">
                      <h3 className="po-preview-section-title">International Trade Details</h3>
                      <div className="po-preview-grid">
                        <div className="po-preview-item">
                          <label>Port of Loading</label>
                          <p>{previewPOData.port_of_loading || previewPOData.po_data?.portOfLoading || 'Not specified'}</p>
                        </div>
                        <div className="po-preview-item">
                          <label>Port of Discharge</label>
                          <p>{previewPOData.port_of_discharge || previewPOData.po_data?.portOfDischarge || 'Not specified'}</p>
                        </div>
                        <div className="po-preview-item">
                          <label>Customs Broker/CHA</label>
                          <p>{previewPOData.customs_broker || previewPOData.po_data?.customsBroker || 'Not specified'}</p>
                        </div>
                        <div className="po-preview-item">
                          <label>HS Code</label>
                          <p>{previewPOData.hs_code || previewPOData.po_data?.hsCode || 'Not specified'}</p>
                        </div>
                        <div className="po-preview-item">
                          <label>Country of Origin</label>
                          <p>{previewPOData.country_of_origin || previewPOData.po_data?.countryOfOrigin || 'Not specified'}</p>
                        </div>
                        <div className="po-preview-item">
                          <label>Incoterms</label>
                          <p>{previewPOData.incoterms || previewPOData.po_data?.incoterms || 'Not specified'}</p>
                        </div>
                        {previewPOData.export_type && (
                          <div className="po-preview-item">
                            <label>Export Type</label>
                            <p>{previewPOData.export_type || previewPOData.po_data?.exportType || 'Not specified'}</p>
                          </div>
                        )}
                        {previewPOData.lut_arn && (
                          <div className="po-preview-item">
                            <label>LUT ARN</label>
                            <p>{previewPOData.lut_arn || previewPOData.po_data?.lutArn || 'Not specified'}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Buyer & Seller Details */}
                  <div className="po-preview-section">
                    <h3 className="po-preview-section-title">Buyer & Seller Information</h3>
                    <div className="po-preview-grid-2">
                      <div className="po-preview-card">
                        <h4 className="po-preview-card-title">Buyer Details</h4>
                        <div className="po-preview-details">
                          <p><strong>Company:</strong> {previewPOData.buyer_company_name || previewPOData.po_data?.buyerCompanyName || 'N/A'}</p>
                          <p><strong>Address:</strong> {previewPOData.buyer_address || previewPOData.po_data?.buyerAddress || 'N/A'}</p>
                          <p><strong>GST:</strong> {previewPOData.buyer_gst_number || previewPOData.po_data?.buyerGstNumber || 'N/A'}</p>
                          <p><strong>State:</strong> {previewPOData.buyer_state || previewPOData.po_data?.buyerState || 'N/A'}</p>
                          <p><strong>Country:</strong> {previewPOData.buyer_country || previewPOData.po_data?.buyerCountry || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="po-preview-card">
                        <h4 className="po-preview-card-title">Seller Details</h4>
                        <div className="po-preview-details">
                          <p><strong>Company:</strong> {previewPOData.seller_company_name || previewPOData.po_data?.sellerCompanyName || 'N/A'}</p>
                          <p><strong>Address:</strong> {previewPOData.seller_address || previewPOData.po_data?.sellerAddress || 'N/A'}</p>
                          <p><strong>GST:</strong> {previewPOData.seller_gst_number || previewPOData.po_data?.sellerGstNumber || 'N/A'}</p>
                          <p><strong>State:</strong> {previewPOData.seller_state || previewPOData.po_data?.sellerState || 'N/A'}</p>
                          <p><strong>Country:</strong> {previewPOData.seller_country || previewPOData.po_data?.sellerCountry || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Line Items */}
                  {previewPOData.line_items && previewPOData.line_items.length > 0 && (
                    <div className="po-preview-section">
                      <h3 className="po-preview-section-title">Line Items</h3>
                      <div className="po-preview-table-wrapper">
                        <table className="po-preview-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Description</th>
                              <th>HSN</th>
                              <th>Qty</th>
                              <th>UOM</th>
                              <th>Unit Price</th>
                              <th>Disc %</th>
                              {isInternationalTrade(previewPOData.trade_scenario || previewPOData.po_data?.tradeScenario) ? (
                                <>
                                  <th>Customs %</th>
                                  <th>IGST %</th>
                                  <th>CESS %</th>
                                </>
                              ) : (previewPOData.trade_scenario || previewPOData.po_data?.tradeScenario) === 'IN Inter-State (Between Indian States)' ? (
                                <>
                                  <th>IGST %</th>
                                  <th>CESS %</th>
                                </>
                              ) : (
                                <>
                                  <th>CGST %</th>
                                  <th>SGST %</th>
                                  <th>CESS %</th>
                                </>
                              )}
                              <th style={{ textAlign: 'right' }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewPOData.line_items.map((item, index) => (
                              <tr key={item.id || item.line_item_id || index}>
                                <td>{index + 1}</td>
                                <td>{item.description || 'N/A'}</td>
                                <td>{item.hsn || item.hsn_code || 'N/A'}</td>
                                <td>{item.quantity || 0}</td>
                                <td>{getUnitLabel(item.uom)}</td>
                                <td>{formatCurrency(item.unit_price || item.unitPrice || 0, previewPOData.currency || previewPOData.po_data?.currency || 'INR')}</td>
                                <td>{item.discount || 0}%</td>
                                {isInternationalTrade(previewPOData.trade_scenario || previewPOData.po_data?.tradeScenario) ? (
                                  <>
                                    <td>{item.customs || item.customsDuty || 0}%</td>
                                    <td>{item.igst || 0}%</td>
                                    <td>{item.cess || 0}%</td>
                                  </>
                                ) : (previewPOData.trade_scenario || previewPOData.po_data?.tradeScenario) === 'IN Inter-State (Between Indian States)' ? (
                                  <>
                                    <td>{item.igst || 0}%</td>
                                    <td>{item.cess || 0}%</td>
                                  </>
                                ) : (
                                  <>
                                    <td>{getCgstPercentage(item, previewPOData.trade_scenario || previewPOData.po_data?.tradeScenario || '').toFixed(2)}%</td>
                                    <td>{getSgstPercentage(item).toFixed(2)}%</td>
                                    <td>{item.cess || 0}%</td>
                                  </>
                                )}
                                <td className="po-table-col-total-cell">{formatCurrency(item.total || 0, previewPOData.currency || previewPOData.po_data?.currency || 'INR')}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            {(() => {
                              const totals = calculatePreviewTotals(previewPOData, previewPOData.line_items);
                              const tradeScenario = previewPOData.trade_scenario || previewPOData.po_data?.tradeScenario || '';
                              const currency = previewPOData.currency || previewPOData.po_data?.currency || 'INR';
                              
                              return (
                                <>
                                  {tradeScenario !== '→IN Import to India (Abroad → India)' && (
                                    <>
                                      <tr className="po-preview-total-row">
                                        <td colSpan={isInternationalTrade(tradeScenario) ? "10" : tradeScenario === 'IN Inter-State (Between Indian States)' ? "9" : "10"} className="po-preview-summary-label">Subtotal (Qty × Unit Price):</td>
                                        <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.subtotal, currency)}</span></td>
                                      </tr>
                                      <tr className="po-preview-total-row">
                                        <td colSpan={isInternationalTrade(tradeScenario) ? "10" : tradeScenario === 'IN Inter-State (Between Indian States)' ? "9" : "10"} className="po-preview-summary-label">Total Discount:</td>
                                        <td className="po-preview-summary-value"><span className="font-semibold">- {formatCurrency(totals.totalDiscount, currency)}</span></td>
                                      </tr>
                                      <tr className="po-preview-total-row">
                                        <td colSpan={isInternationalTrade(tradeScenario) ? "10" : tradeScenario === 'IN Inter-State (Between Indian States)' ? "9" : "10"} className="po-preview-summary-label"><strong>Taxable Amount:</strong></td>
                                        <td className="po-preview-summary-value"><span className="font-semibold"><strong>{formatCurrency(totals.taxableAmount, currency)}</strong></span></td>
                                      </tr>
                                    </>
                                  )}
                                  {isInternationalTrade(tradeScenario) ? (
                                    <>
                                      {tradeScenario === '→IN Import to India (Abroad → India)' && (
                                        <>
                                          <tr className="po-preview-total-row">
                                            <td colSpan="10" className="po-preview-summary-label">Subtotal (Qty × Unit Price):</td>
                                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.subtotal, currency)}</span></td>
                                          </tr>
                                          <tr className="po-preview-total-row">
                                            <td colSpan="10" className="po-preview-summary-label">Total Discount:</td>
                                            <td className="po-preview-summary-value"><span className="font-semibold">- {formatCurrency(totals.totalDiscount, currency)}</span></td>
                                          </tr>
                                          <tr className="po-preview-total-row">
                                            <td colSpan="10" className="po-preview-summary-label"><strong>Taxable Amount (FOB Value):</strong></td>
                                            <td className="po-preview-summary-value"><span className="font-semibold"><strong>{formatCurrency(totals.taxableAmount, currency)}</strong></span></td>
                                          </tr>
                                        </>
                                      )}
                                      <tr className="po-preview-total-row">
                                        <td colSpan="10" className="po-preview-summary-label">Basic Customs Duty (BCD):</td>
                                        <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.basicCustomsDuty || 0, currency)}</span></td>
                                      </tr>
                                      <tr className="po-preview-total-row">
                                        <td colSpan="10" className="po-preview-summary-label">Social Welfare Surcharge (10% of BCD):</td>
                                        <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.socialWelfareSurcharge || 0, currency)}</span></td>
                                      </tr>
                                      <tr className="po-preview-total-row">
                                        <td colSpan="10" className="po-preview-summary-label">Assessable Value (CIF + BCD + SWS):</td>
                                        <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.assessableValue || 0, currency)}</span></td>
                                      </tr>
                                      <tr className="po-preview-total-row">
                                        <td colSpan="10" className="po-preview-summary-label">IGST (on Assessable Value):</td>
                                        <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.igstOnAssessable || 0, currency)}</span></td>
                                      </tr>
                                    </>
                                  ) : tradeScenario === 'IN Inter-State (Between Indian States)' ? (
                                    <>
                                      <tr className="po-preview-total-row">
                                        <td colSpan="9" className="po-preview-summary-label">IGST:</td>
                                        <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.igst, currency)}</span></td>
                                      </tr>
                                      <tr className="po-preview-total-row">
                                        <td colSpan="9" className="po-preview-summary-label">CESS:</td>
                                        <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.cess, currency)}</span></td>
                                      </tr>
                                    </>
                                  ) : (
                                    <>
                                      <tr className="po-preview-total-row">
                                        <td colSpan="10" className="po-preview-summary-label">CGST:</td>
                                        <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.cgst, currency)}</span></td>
                                      </tr>
                                      <tr className="po-preview-total-row">
                                        <td colSpan="10" className="po-preview-summary-label">SGST:</td>
                                        <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.sgst, currency)}</span></td>
                                      </tr>
                                      <tr className="po-preview-total-row">
                                        <td colSpan="10" className="po-preview-summary-label">CESS:</td>
                                        <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.cess, currency)}</span></td>
                                      </tr>
                                    </>
                                  )}
                                  <tr className="po-preview-total-row po-preview-grand-total">
                                    <td colSpan={isInternationalTrade(tradeScenario) ? "10" : tradeScenario === 'IN Inter-State (Between Indian States)' ? "9" : "10"} className="po-preview-summary-label">
                                      <span className="font-semibold">Grand Total (Sum of Line Items):</span>
                                    </td>
                                    <td className="po-preview-summary-value">
                                      <span className="font-semibold">{formatCurrency(totals.grandTotal, currency)}</span>
                                    </td>
                                  </tr>
                                </>
                              );
                            })()}
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Delivery Schedule */}
                  {previewPOData.delivery_schedules && previewPOData.delivery_schedules.length > 0 && (
                    <div className="po-preview-section">
                      <h3 className="po-preview-section-title">Delivery Schedule</h3>
                      <div className="po-preview-table-wrapper">
                        <table className="po-preview-table">
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th>Quantity</th>
                              <th>Delivery Date</th>
                              <th>Location</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewPOData.delivery_schedules.map((schedule, idx) => {
                              const item = previewPOData.line_items?.find(i => (i.id || i.line_item_id)?.toString() === schedule.item?.toString());
                              return (
                                <tr key={schedule.id || idx}>
                                  <td>{item ? item.description : `Item ${schedule.item}`}</td>
                                  <td>{schedule.quantity || 0}</td>
                                  <td>{formatDateForPreview(schedule.deliveryDate || schedule.delivery_date)}</td>
                                  <td>{schedule.location || 'N/A'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Terms & Conditions */}
                  {previewPOData.terms_conditions && previewPOData.terms_conditions.length > 0 && (
                    <div className="po-preview-section">
                      <h3 className="po-preview-section-title">Terms & Conditions</h3>
                      <ol className="po-preview-terms-list">
                        {previewPOData.terms_conditions.map((term, index) => (
                          <li key={term.id || index}>{term.text || term || 'N/A'}</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Special Instructions */}
                  {previewPOData.special_instructions && (
                    <div className="po-preview-section">
                      <h3 className="po-preview-section-title">Special Instructions</h3>
                      <p className="po-preview-text">{previewPOData.special_instructions || previewPOData.po_data?.specialInstructions || 'N/A'}</p>
                    </div>
                  )}

                  {/* Attachments */}
                  {previewPOData.attachments && previewPOData.attachments.length > 0 && (
                    <div className="po-preview-section">
                      <h3 className="po-preview-section-title">Attachments</h3>
                      <div className="po-preview-attachments-list">
                        {previewPOData.attachments.map((attachment, idx) => {
                          const fileName = attachment.name || attachment.fileName || attachment || `Attachment ${idx + 1}`;
                          return (
                            <div key={attachment.id || idx} className="po-preview-attachment-item">
                              <div className="po-preview-attachment-info">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-preview-attachment-icon">
                                  <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                                  <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                                  <path d="M10 9H8"></path>
                                  <path d="M16 13H8"></path>
                                  <path d="M16 17H8"></path>
                                </svg>
                                <span className="po-preview-attachment-name">{fileName}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="po-preview-modal-footer">
              <button className="po-btn-secondary" onClick={() => setShowPreviewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Seller Accepted Details Modal */}
      {showAcceptedDetailsModal && (
        <div className="po-preview-modal-overlay" onClick={() => setShowAcceptedDetailsModal(false)}>
          <div className="po-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="po-preview-modal-header">
              <div className="po-preview-modal-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-preview-icon">
                  <path d="M9 11l3 3L22 4"></path>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                </svg>
                <div>
                  <h2 className="po-preview-modal-title">Seller Accepted Information</h2>
                  <p className="po-preview-modal-subtitle">View seller accepted purchase order details</p>
                </div>
              </div>
              <button className="po-preview-modal-close" onClick={() => setShowAcceptedDetailsModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="po-preview-modal-body">
              {loadingAcceptedDetails ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p style={{ marginTop: '16px', color: '#666' }}>Loading accepted details...</p>
                </div>
              ) : acceptedDetailsData ? (
                <>
                <div className="po-preview-section">
                  <h3 className="po-preview-section-title">Acceptance Information</h3>
                  <div className="po-preview-grid">
                    <div className="po-preview-item">
                      <label>PO Acceptance Date</label>
                      <p>{acceptedDetailsData.po_date ? formatDate(acceptedDetailsData.po_date) : (acceptedDetailsData.accepted_at ? formatDate(acceptedDetailsData.accepted_at) : 'N/A')}</p>
                    </div>
                    <div className="po-preview-item">
                      <label>PO Acknowledgement No</label>
                      <p>{acceptedDetailsData.po_ack_number || 'N/A'}</p>
                    </div>
                    <div className="po-preview-item">
                      <label>Estimated Dispatch Date</label>
                      <p>{acceptedDetailsData.estimated_dispatch_date ? formatDate(acceptedDetailsData.estimated_dispatch_date) : 'N/A'}</p>
                    </div>
                    <div className="po-preview-item">
                      <label>Estimated Delivery Date</label>
                      <p>{acceptedDetailsData.estimated_delivery_date_seller ? formatDate(acceptedDetailsData.estimated_delivery_date_seller) : 'N/A'}</p>
                    </div>
                    <div className="po-preview-item" style={{ gridColumn: '1 / -1' }}>
                      <label>Additional Notes</label>
                      <p>{acceptedDetailsData.acceptance_notes || 'N/A'}</p>
                    </div>
                  </div>
                </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#666' }}>No accepted details available.</p>
                </div>
              )}
            </div>

            <div className="po-preview-modal-footer">
              <button className="po-btn-secondary" onClick={() => setShowAcceptedDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Seller Rejected Details Modal */}
      {showRejectedDetailsModal && (
        <div className="po-preview-modal-overlay" onClick={() => setShowRejectedDetailsModal(false)}>
          <div className="po-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="po-preview-modal-header">
              <div className="po-preview-modal-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-preview-icon">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <div>
                  <h2 className="po-preview-modal-title">Seller Rejected Information</h2>
                  <p className="po-preview-modal-subtitle">View seller rejected purchase order details</p>
                </div>
              </div>
              <button className="po-preview-modal-close" onClick={() => setShowRejectedDetailsModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="po-preview-modal-body">
              {loadingRejectedDetails ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p style={{ marginTop: '16px', color: '#666' }}>Loading rejected details...</p>
                </div>
              ) : rejectedDetailsData ? (
                <div className="po-preview-section">
                  <h3 className="po-preview-section-title">Rejection Information</h3>
                  <div className="po-preview-grid">
                    <div className="po-preview-item">
                      <label>PO Rejection Date</label>
                      <p>{rejectedDetailsData.rejected_at ? formatDate(rejectedDetailsData.rejected_at) : 'N/A'}</p>
                    </div>
                    <div className="po-preview-item">
                      <label>Rejected By</label>
                      <p>{rejectedDetailsData.rejected_by || 'N/A'}</p>
                    </div>
                    <div className="po-preview-item" style={{ gridColumn: '1 / -1' }}>
                      <label>Rejection Reason</label>
                      <p>{rejectedDetailsData.rejection_reason || 'N/A'}</p>
                    </div>
                    <div className="po-preview-item" style={{ gridColumn: '1 / -1' }}>
                      <label>Rejection Details</label>
                      <p>{rejectedDetailsData.rejection_details || 'N/A'}</p>
                    </div>
                    {rejectedDetailsData.rejection_attachment && (
                      <div className="po-preview-item" style={{ gridColumn: '1 / -1' }}>
                        <label>Rejection Attachment</label>
                        <p>{rejectedDetailsData.rejection_attachment}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#666' }}>No rejected details available.</p>
                </div>
              )}
            </div>

            <div className="po-preview-modal-footer">
              <button className="po-btn-secondary" onClick={() => setShowRejectedDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
